import {
  type SidebarDefinition,
  type SidebarSnapshot,
  defineSidebar,
  readSidebar,
} from "@/types/sidebar-session";
import type { UserTiny } from "@/types/user";

export enum PatientSidebarCommand {
  UPDATE = "UPDATE",
}

export interface PatientSidebarPayload {
  patientTiny: UserTiny | null;
  patientId: string | null;
  selectedEventId: string | null;
  autoExpandSection: string | null;
  hideUpcomingEvents: boolean;
}

/** Session membership stores the patient once; selectors derive its ID. */
export type PatientSidebarEntryPayload = Omit<
  PatientSidebarPayload,
  "patientId"
>;

type PatientSidebarInput =
  | {
      patientTiny: UserTiny;
      selectedEventId?: string | null;
      autoExpandSection?: string | null;
    }
  | {
      command: PatientSidebarCommand.UPDATE;
      patch: Partial<Omit<PatientSidebarPayload, "patientId">>;
    };

const normalizedPatients = new WeakMap<
  PatientSidebarEntryPayload,
  PatientSidebarPayload
>();

const emptyPatient: PatientSidebarPayload = {
  patientTiny: null,
  patientId: null,
  selectedEventId: null,
  autoExpandSection: null,
  hideUpcomingEvents: false,
};

export const patientSidebarDefinition: SidebarDefinition<
  PatientSidebarInput,
  PatientSidebarEntryPayload
> = defineSidebar<PatientSidebarInput, PatientSidebarEntryPayload>({
  id: "PATIENT",
  label: "Patient",
  order: 10,
  strategy: {
    planOpen(ctx, input) {
      const existing = ctx.read(patientSidebarDefinition);
      if ("command" in input) {
        if (!existing) return {};
        return {
          upsert: [
            ctx.write(patientSidebarDefinition, {
              ...existing.payload,
              ...input.patch,
            }),
          ],
        };
      }
      return {
        upsert: [
          ctx.write(patientSidebarDefinition, {
            patientTiny: input.patientTiny,
            selectedEventId: input.selectedEventId ?? null,
            autoExpandSection: input.autoExpandSection ?? null,
            hideUpcomingEvents: !!input.autoExpandSection,
          }),
        ],
        remove:
          existing?.payload.patientTiny &&
          existing.payload.patientTiny.id !== input.patientTiny.id
            ? ["TASK"]
            : [],
        select: patientSidebarDefinition.id,
      };
    },
  },
});

export function selectPatientSidebar(
  state: Pick<SidebarSnapshot, "entries">,
): PatientSidebarPayload {
  const payload = readSidebar(state, patientSidebarDefinition)?.payload;
  if (!payload) return emptyPatient;
  let normalized = normalizedPatients.get(payload);
  if (!normalized) {
    normalized = { ...payload, patientId: payload.patientTiny?.id ?? null };
    normalizedPatients.set(payload, normalized);
  }
  return normalized;
}
